// ============================================
// MISLINKS.JS — Tabla de links compartidos por el vendedor + stats
// ============================================

import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './utils/storage';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloBotonSecundario, estiloTarjeta,
} from './components/helpersClientes';
import { useUsuario } from './context/UsuarioContext';
import { ligaPublica } from './config/api';

function urlPublica(tipo, codigo) {
  return ligaPublica(tipo === 'producto' ? 'p' : 'v', codigo);
}

function fmtFecha(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

function hace(iso) {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

export default function MisLinks() {
  const { usuario } = useUsuario();
  const [links, setLinks] = useState([]);
  const [stats, setStats] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [toast, setToast] = useState(null);

  const vendedorId = usuario?.correo || usuario?.email || usuario?.nombre || 'sin-vendedor';

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(
        `${API_BASE_CLI}/api/compartir/mios?vendedor_id=${encodeURIComponent(vendedorId)}`
      ).then(x => x.json());
      if (r.ok) {
        setLinks(r.links || []);
        setStats(r.stats || null);
      }
    } finally { setCargando(false); }
  }, [vendedorId]);

  useEffect(() => { cargar(); }, [cargar]);

  const mostrarToast = (m) => { setToast(m); setTimeout(() => setToast(null), 2200); };

  const copiarLink = async (link) => {
    const url = urlPublica(link.tipo, link.codigo_corto);
    try {
      await navigator.clipboard.writeText(url);
      mostrarToast('✅ Link copiado');
    } catch {
      mostrarToast('⚠️ No se pudo copiar');
    }
  };

  const eliminarLink = async (link) => {
    if (!window.confirm(`¿Eliminar el link "${link.titulo || link.codigo_corto}"?`)) return;
    const r = await fetch(`${API_BASE_CLI}/api/compartir/${link.id}`, { method: 'DELETE', headers: { ...authHeaders() } }).then(x => x.json());
    if (r.ok) {
      mostrarToast('✅ Link eliminado');
      cargar();
    } else {
      mostrarToast('⚠️ ' + (r.error || 'error'));
    }
  };

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS, padding: 20, background: COLOR_FONDO, minHeight: '100%' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>
          🔗 Mis links compartidos
        </div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
          Productos y vitrinas que has compartido con clientes. Cada vista queda registrada.
        </div>
      </div>

      {/* Stats arriba */}
      {stats && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12,
          marginBottom: 18,
        }}>
          <Card titulo="Total links" valor={stats.total_links} color={COLOR_PRINCIPAL} />
          <Card titulo="Total vistas" valor={stats.total_vistas} color="#16a34a" />
          {stats.mas_visto && (
            <Card
              titulo="Más visto"
              valor={`${stats.mas_visto.vistas} vistas`}
              detalle={stats.mas_visto.titulo}
              color="#f59e0b"
            />
          )}
        </div>
      )}

      {/* Tabla */}
      <div style={{ ...estiloTarjeta, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: COLOR_FONDO }}>
              <tr>
                <th style={th}>Fecha</th>
                <th style={th}>Tipo</th>
                <th style={th}>Título</th>
                <th style={th}>Cliente</th>
                <th style={{ ...th, textAlign: 'right' }}>Vistas</th>
                <th style={th}>Última vista</th>
                <th style={{ ...th, textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {links.length === 0 && !cargando && (
                <tr>
                  <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO }}>
                    Aún no has compartido nada. Desde <strong>La Tienda</strong> puedes generar links de productos y vitrinas.
                  </td>
                </tr>
              )}
              {links.map(l => (
                <tr key={l.id} style={{
                  borderTop: `1px solid ${COLOR_BORDE}`,
                  opacity: l.activo ? 1 : 0.55,
                }}>
                  <td style={td}>{fmtFecha(l.creado_en)}</td>
                  <td style={td}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                      background: l.tipo === 'producto' ? '#e6f1fb' : '#fff2d4',
                      color: l.tipo === 'producto' ? '#2f5f9e' : '#a86b1e',
                    }}>
                      {l.tipo === 'producto' ? 'PRODUCTO' : 'VITRINA'}
                    </span>
                  </td>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: COLOR_OSCURO, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.titulo || `(sin título)`}
                    </div>
                    <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, fontFamily: 'monospace' }}>
                      /{l.tipo === 'producto' ? 'p' : 'v'}/{l.codigo_corto}
                    </div>
                  </td>
                  <td style={td}>{l.cliente_nombre || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: COLOR_OSCURO }}>
                    {l.vistas || 0}
                  </td>
                  <td style={td}>
                    {l.ultima_vista ? (
                      <div>
                        <div>{hace(l.ultima_vista)}</div>
                        <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO }}>
                          {fmtFecha(l.ultima_vista)}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: COLOR_GRIS_CLARO }}>—</span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <a
                      href={urlPublica(l.tipo, l.codigo_corto)}
                      target="_blank" rel="noopener noreferrer"
                      style={{ ...estiloBotonSecundario, fontSize: 11, marginRight: 4, textDecoration: 'none' }}
                    >Ver</a>
                    <button
                      style={{ ...estiloBotonSecundario, fontSize: 11, marginRight: 4 }}
                      onClick={() => copiarLink(l)}
                    >📋 Copiar</button>
                    <button
                      style={{ ...estiloBotonSecundario, fontSize: 11, color: '#e05c5c' }}
                      onClick={() => eliminarLink(l)}
                    >🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c', color: '#fff', padding: '10px 16px', borderRadius: 10,
          fontSize: 12, zIndex: 1500, fontFamily: PP,
        }}>{toast}</div>
      )}
    </div>
  );
}

function Card({ titulo, valor, detalle, color }) {
  return (
    <div style={{ ...estiloTarjeta, padding: 14, borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
        {titulo}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: COLOR_OSCURO }}>{valor}</div>
      {detalle && (
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {detalle}
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: COLOR_GRIS, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 };
const td = { padding: '10px 12px', verticalAlign: 'middle', color: COLOR_GRIS };
